import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useRef, useState, useCallback } from 'react';
import { agentApi, conversationApi, skillApi, guardrailApi, toolApi, agentToolApi, type Agent, type ConversationDetail, type ConversationEvent, type Skill, type AgentSkillConfig, type AgentToolConfig, type ToolDefinition, type Guardrail } from '@/lib/api';
import { useNotification } from '@/contexts/NotificationContext';
import {
  Bot, Settings, Play, Circle, Send, ArrowLeft,
  Zap, Clock, Hash, AlertTriangle, CheckCircle, MessageSquare, User,
  Wrench, Plus, Trash2, ShieldCheck, Shield, BookOpen, Code2, Webhook, ArrowRight,
  Network, RefreshCw, Plug, ChevronDown, Save
} from 'lucide-react';
import styles from './_auth.agents.$agentId.module.css';

export const Route = createFileRoute('/_auth/agents/$agentId')({
  component: AgentDetailPage,
});

type Tab = 'config' | 'orchestration' | 'tools' | 'skills' | 'flow' | 'test';

const EVENT_COLORS: Record<string, string> = {
  CONVERSATION_STARTED:    'var(--color-cyan)',
  MESSAGE_RECEIVED:        'var(--color-primary)',
  MODEL_REQUEST_STARTED:   'var(--color-warning)',
  MODEL_REQUEST_COMPLETED: 'var(--color-success)',
  MODEL_REQUEST_FAILED:    'var(--color-error)',
  MESSAGE_SENT:            'var(--color-success)',
  HANDOFF_CREATED:         'var(--color-error)',
};

/** Lightweight markdown → HTML for agent chat bubbles.
 *  Handles: **bold**, *italic*, `code`, bullet lists, numbered lists, line breaks.
 *  XSS-safe: raw text is escaped before transformation. */
function renderMarkdown(raw: string): string {
  // 1. Escape HTML entities first
  const esc = raw
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  const lines = esc.split('\n');
  const out: string[] = [];
  let inUl = false, inOl = false;

  const closeList = () => {
    if (inUl) { out.push('</ul>'); inUl = false; }
    if (inOl) { out.push('</ol>'); inOl = false; }
  };

  const inlineFormat = (s: string) =>
    s
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/`([^`]+)`/g, '<code>$1</code>');

  for (const line of lines) {
    const ul = line.match(/^[\s]*[-•*]\s+(.*)/);
    const ol = line.match(/^[\s]*\d+\.\s+(.*)/);
    const blank = line.trim() === '';

    if (ul) {
      if (!inUl) { closeList(); out.push('<ul>'); inUl = true; }
      out.push(`<li>${inlineFormat(ul[1])}</li>`);
    } else if (ol) {
      if (!inOl) { closeList(); out.push('<ol>'); inOl = true; }
      out.push(`<li>${inlineFormat(ol[1])}</li>`);
    } else if (blank) {
      closeList();
      // blank line = paragraph break (handled below)
      out.push('');
    } else {
      closeList();
      out.push(`<p>${inlineFormat(line)}</p>`);
    }
  }
  closeList();

  // Collapse consecutive empty strings (paragraph gaps already expressed via <p>)
  return out.filter((l, i) => !(l === '' && out[i - 1] === '')).join('\n');
}



const EVENT_ICONS: Record<string, React.ReactNode> = {
  CONVERSATION_STARTED:    <MessageSquare size={12} />,
  MESSAGE_RECEIVED:        <User size={12} />,
  MODEL_REQUEST_STARTED:   <Zap size={12} />,
  MODEL_REQUEST_COMPLETED: <CheckCircle size={12} />,
  MODEL_REQUEST_FAILED:    <AlertTriangle size={12} />,
  MESSAGE_SENT:            <Bot size={12} />,
  HANDOFF_CREATED:         <AlertTriangle size={12} />,
};

export default function AgentDetailPage() {
  const { agentId } = Route.useParams();
  const [agent, setAgent] = useState<Agent | null>(null);
  const [tab, setTab] = useState<Tab>('config');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Config edit state
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editPrompt, setEditPrompt] = useState('');
  const [editMaxTurns, setEditMaxTurns] = useState(10);

  // Test console state
  const [testConv, setTestConv] = useState<ConversationDetail | null>(null);
  const [testInput, setTestInput] = useState('');
  const [testLoading, setTestLoading] = useState(false);
  const [testError, setTestError] = useState<{ message: string; resolutionHint?: string } | null>(null);
  const [events, setEvents] = useState<ConversationEvent[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { error: notifyError, success: notifySuccess } = useNotification();

  // Skills tab state
  const [allSkills, setAllSkills] = useState<Skill[]>([]);
  const [agentSkills, setAgentSkills] = useState<AgentSkillConfig[]>([]);
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [showSkillPicker, setShowSkillPicker] = useState(false);
  const [attachingSkillId, setAttachingSkillId] = useState<string | null>(null);

  // Tools tab state
  const [allTools, setAllTools] = useState<ToolDefinition[]>([]);
  const [agentTools, setAgentTools] = useState<AgentToolConfig[]>([]);
  const [toolsLoading, setToolsLoading] = useState(false);
  const [expandedToolId, setExpandedToolId] = useState<string | null>(null);
  const [editToolInstructions, setEditToolInstructions] = useState('');
  const [editToolDefaultParams, setEditToolDefaultParams] = useState<Record<string, string>>({});
  const [savingToolConfig, setSavingToolConfig] = useState(false);

  // Flow tab state
  const [allGuardrails, setAllGuardrails] = useState<Guardrail[]>([]);
  const [flowLoading, setFlowLoading] = useState(false);

  useEffect(() => {
    agentApi.get(agentId)
      .then(a => {
        setAgent(a);
        setEditName(a.name);
        setEditDesc(a.description ?? '');
        setEditPrompt(a.orchestrationConfig?.systemPrompt ?? '');
        setEditMaxTurns(a.orchestrationConfig?.maxTurnsBeforeEscalation ?? 10);
      })
      .finally(() => setLoading(false));
  }, [agentId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [testConv?.messages]);

  async function handleActivate() {
    if (!agent) return;
    setSaving(true);
    const updated = await agentApi.update(agentId, {
      status: agent.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE',
    });
    setAgent(updated);
    setSaving(false);
  }

  async function handleSaveConfig() {
    setSaving(true);
    const updated = await agentApi.update(agentId, { name: editName, description: editDesc });
    setAgent(updated);
    setSaving(false);
  }

  async function handleSaveOrchestration() {
    setSaving(true);
    await agentApi.upsertOrchestration(agentId, { systemPrompt: editPrompt, maxTurnsBeforeEscalation: editMaxTurns });
    const updated = await agentApi.get(agentId);
    setAgent(updated);
    setSaving(false);
  }

  const loadSkills = useCallback(async () => {
    setSkillsLoading(true);
    try {
      const [all, attached] = await Promise.all([
        skillApi.list(),
        skillApi.agentSkills(agentId),
      ]);
      setAllSkills(all.filter(s => s.active));
      setAgentSkills(attached);
    } finally {
      setSkillsLoading(false);
    }
  }, [agentId]);

  const loadFlowData = useCallback(async () => {
    setFlowLoading(true);
    try {
      const [attachedSkills, listGuardrails] = await Promise.all([
        skillApi.agentSkills(agentId),
        guardrailApi.list(),
      ]);
      setAgentSkills(attachedSkills);
      setAllGuardrails(listGuardrails);
    } catch (e) {
      notifyError('Failed to load flow mapping', (e as Error).message);
    } finally {
      setFlowLoading(false);
    }
  }, [agentId, notifyError]);

  useEffect(() => {
    if (tab === 'flow') void loadFlowData();
  }, [tab, loadFlowData]);

  const loadTools = useCallback(async () => {
    setToolsLoading(true);
    try {
      const [all, attached] = await Promise.all([
        toolApi.list(),
        agentToolApi.list(agentId),
      ]);
      setAllTools(all);
      setAgentTools(attached);
    } finally {
      setToolsLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    if (tab === 'skills') void loadSkills();
    if (tab === 'tools') void loadTools();
  }, [tab, loadSkills, loadTools]);

  async function handleToggleTool(tool: ToolDefinition) {
    const attached = agentTools.find(at => at.toolId === tool.id);
    if (attached) {
      // Detach — optimistic
      setAgentTools(prev => prev.filter(at => at.toolId !== tool.id));
      try {
        await agentToolApi.detach(agentId, tool.id);
        notifySuccess('Tool removed', `"${tool.displayName}" detached from this agent.`);
      } catch (e) {
        setAgentTools(prev => [...prev, attached]);
        notifyError('Failed to remove tool', (e as Error).message);
      }
    } else {
      // Attach — optimistic
      const optimistic: AgentToolConfig = {
        id: `opt-${tool.id}`, tenantId: '', agentId, toolId: tool.id,
        enabled: true, instructions: null, defaultParams: null,
        createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(), tool,
      };
      setAgentTools(prev => [...prev, optimistic]);
      try {
        await agentToolApi.attach(agentId, tool.id);
        notifySuccess('Tool attached', `"${tool.displayName}" is now available to this agent.`);
        void loadTools();
      } catch (e) {
        setAgentTools(prev => prev.filter(at => at.toolId !== tool.id));
        notifyError('Failed to attach tool', (e as Error).message);
      }
    }
  }

  async function handleAttachSkill(skillId: string) {
    // Prevent double-click
    if (attachingSkillId) return;
    setAttachingSkillId(skillId);

    // Optimistic update — immediately hide from picker
    const skillDef = allSkills.find(s => s.id === skillId);
    const optimisticEntry: AgentSkillConfig = {
      id: `optimistic-${skillId}`,
      agentId,
      skillId,
      enabled: true,
      sortOrder: agentSkills.length,
      skill: skillDef!,
    };
    setAgentSkills(prev => [...prev, optimisticEntry]);

    try {
      await skillApi.attachSkill(agentId, skillId);
      notifySuccess('Skill attached', `"${skillDef?.name ?? 'Skill'}" is now available to this agent.`);
      setShowSkillPicker(false);
      // Refresh to get real IDs from server
      void loadSkills();
    } catch (e) {
      // Roll back optimistic entry
      setAgentSkills(prev => prev.filter(s => s.id !== optimisticEntry.id));
      const raw = (e as Error).message ?? '';
      // Humanize conflict error — strip UUIDs
      const message = raw.includes('already attached')
        ? `"${skillDef?.name ?? 'This skill'}" is already attached to this agent.`
        : raw;
      notifyError('Failed to attach skill', message);
    } finally {
      setAttachingSkillId(null);
    }
  }

  async function handleDetachSkill(skillId: string) {
    // Optimistic removal — card disappears instantly
    const removed = agentSkills.find(s => s.skillId === skillId);
    setAgentSkills(prev => prev.filter(s => s.skillId !== skillId));
    try {
      await skillApi.detachSkill(agentId, skillId);
      notifySuccess('Skill removed', `"${removed?.skill.name ?? 'Skill'}" has been detached from this agent.`);
    } catch (e) {
      // Roll back
      if (removed) setAgentSkills(prev => [...prev, removed]);
      notifyError('Failed to remove skill', (e as Error).message);
    }
  }

  async function handleStartTest() {
    setTestConv(null);
    setTestError(null);
    setEvents([]);
    setTestLoading(true);
    const result = await conversationApi.start({ agentId, channel: 'WEB_CHAT' });
    const detail = await conversationApi.get(result.conversation.id);
    setTestConv(detail);
    setEvents(detail.events);
    setTestLoading(false);
  }

  async function handleSendTest() {
    if (!testConv || !testInput.trim()) return;
    const msg = testInput.trim();
    setTestInput('');
    // Optimistically show the user message immediately
    setTestConv({
      ...testConv,
      messages: [
        ...testConv.messages,
        { id: `temp-${Date.now()}`, senderType: 'USER', content: msg, createdAt: new Date().toISOString() },
      ],
    });
    setTestLoading(true);
    setTestError(null);
    try {
      await conversationApi.sendMessage(testConv.id, msg);
      const updated = await conversationApi.get(testConv.id);
      setTestConv(updated);
      setEvents(updated.events);
    } catch (err: unknown) {
      const e = err as { message?: string; resolutionHint?: string; status?: number };
      const message = e?.message ?? 'Something went wrong';
      const resolutionHint = e?.resolutionHint;
      setTestError({ message, resolutionHint });
      notifyError('Agent error', message, resolutionHint);
    } finally {
      setTestLoading(false);
    }
  }

  async function handleToggleSkill(skillId: string, currentEnabled: boolean) {
    try {
      await skillApi.updateAgentSkillConfig(agentId, skillId, { enabled: !currentEnabled });
      await loadSkills();
      notifySuccess(
        !currentEnabled ? 'Skill enabled' : 'Skill disabled',
        !currentEnabled ? 'The skill is now active for this agent.' : 'The skill has been paused for this agent.',
      );
    } catch (e) {
      notifyError('Failed to update skill', (e as Error).message);
    }
  }

  if (loading || !agent) {
    return <div className={styles.loading}><Bot size={32} className={styles.loadingIcon} /><span>Loading agent…</span></div>;
  }

  const isActive = agent.status === 'ACTIVE';

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <Link to="/agents" className={styles.back}><ArrowLeft size={16} /> Agents</Link>
        <div className={styles.titleRow}>
          <div className={styles.agentIcon}><Bot size={20} /></div>
          <div>
            <h1 className={styles.title}>{agent.name}</h1>
            <p className={styles.subtitle}>{agent.description ?? 'No description'}</p>
          </div>
          <div className={styles.headerActions}>
            <div className={styles.statusBadge} style={{ '--dot': isActive ? 'var(--color-success)' : 'var(--color-muted)' } as React.CSSProperties}>
              <Circle size={7} fill="currentColor" />{agent.status}
            </div>
            <button
              id="toggle-status"
              className={`${styles.actionBtn} ${isActive ? styles.pauseBtn : styles.activateBtn}`}
              onClick={handleActivate}
              disabled={saving}
            >
              {isActive ? 'Pause' : 'Activate'}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className={styles.tabs}>
          {(['config', 'orchestration', 'tools', 'skills', 'flow', 'test'] as Tab[]).map(t => (
            <button
              key={t}
              id={`tab-${t}`}
              className={`${styles.tab} ${tab === t ? styles.activeTab : ''}`}
              onClick={() => setTab(t)}
            >
              {t === 'config' && <Settings size={14} />}
              {t === 'orchestration' && <Zap size={14} />}
              {t === 'tools' && <Plug size={14} />}
              {t === 'skills' && <Wrench size={14} />}
              {t === 'flow' && <Network size={14} />}
              {t === 'test' && <Play size={14} />}
              {t.charAt(0).toUpperCase() + t.slice(1)}
              {t === 'tools' && agentTools.length > 0 && (
                <span style={{
                  background: 'var(--color-primary)',
                  color: '#fff',
                  fontSize: 10,
                  fontWeight: 700,
                  borderRadius: 8,
                  padding: '1px 6px',
                  marginLeft: 2,
                }}>{agentTools.length}</span>
              )}
              {t === 'skills' && agentSkills.length > 0 && (
                <span style={{
                  background: 'var(--color-primary)',
                  color: '#fff',
                  fontSize: 10,
                  fontWeight: 700,
                  borderRadius: 8,
                  padding: '1px 6px',
                  marginLeft: 2,
                }}>{agentSkills.length}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className={styles.body}>
        {/* Config tab */}
        {tab === 'config' && (
          <div className={styles.panel}>
            <h2 className={styles.panelTitle}>Agent Configuration</h2>
            <div className={styles.fields}>
              <div className={styles.field}>
                <label className={styles.label}>Name</label>
                <input className={styles.input} value={editName} onChange={e => setEditName(e.target.value)} />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Description</label>
                <textarea className={styles.textarea} value={editDesc} onChange={e => setEditDesc(e.target.value)} rows={3} />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Routing Channels</label>
                <p className={styles.hint}>{agent.routingChannels.length > 0 ? agent.routingChannels.join(', ') : 'All channels'}</p>
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Priority</label>
                <p className={styles.hint}>{agent.routingPriority}</p>
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Total conversations</label>
                <p className={styles.hint}>{agent._count?.conversations ?? 0}</p>
              </div>
            </div>
            <button id="save-config" className={styles.saveBtn} onClick={handleSaveConfig} disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        )}

        {/* Orchestration tab */}
        {tab === 'orchestration' && (
          <div className={styles.panel}>
            <h2 className={styles.panelTitle}>Orchestration Config</h2>
            <div className={styles.fields}>
              <div className={styles.field}>
                <label className={styles.label}>System Prompt</label>
                <p className={styles.hint}>The core instruction given to the LLM at the start of every conversation.</p>
                <textarea
                  id="system-prompt"
                  className={`${styles.textarea} ${styles.promptArea}`}
                  value={editPrompt}
                  onChange={e => setEditPrompt(e.target.value)}
                  rows={12}
                  placeholder="You are a helpful assistant…"
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Max turns before escalation</label>
                <p className={styles.hint}>After this many conversation turns, auto-create a handoff for a human operator.</p>
                <input
                  id="max-turns"
                  type="number"
                  className={styles.input}
                  style={{ maxWidth: 120 }}
                  value={editMaxTurns}
                  onChange={e => setEditMaxTurns(Number(e.target.value))}
                  min={1}
                  max={100}
                />
              </div>
            </div>
            <button id="save-orchestration" className={styles.saveBtn} onClick={handleSaveOrchestration} disabled={saving}>
              {saving ? 'Saving…' : 'Save Orchestration Config'}
            </button>
          </div>
        )}

        {/* Tools tab */}
        {tab === 'tools' && (
          <div className={styles.panel} style={{ maxWidth: 800 }}>
            <div style={{ marginBottom: '1.25rem' }}>
              <h2 className={styles.panelTitle} style={{ marginBottom: 4 }}>Connected Tools</h2>
              <p className={styles.hint}>Toggle tools on/off, then expand to configure <strong>when</strong> the agent should use each tool and <strong>what defaults</strong> to send. Activate tools from the <Link to="/settings/tools-catalog" style={{ color: 'var(--color-primary)' }}>Tool Catalog</Link> first.</p>
            </div>

            {toolsLoading && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[1,2,3].map(i => <div key={i} style={{ height: 60, borderRadius: 10, background: 'var(--surface-2)', opacity: 0.5 }} />)}
              </div>
            )}

            {!toolsLoading && allTools.length === 0 && (
              <div style={{
                border: '2px dashed var(--border)',
                borderRadius: 12,
                padding: '2.5rem',
                textAlign: 'center',
                color: 'var(--color-muted)',
              }}>
                <Plug size={32} style={{ margin: '0 auto 12px', opacity: 0.4, display: 'block' }} />
                <p style={{ fontWeight: 600, marginBottom: 6 }}>No tools available</p>
                <p style={{ fontSize: 13 }}>Go to the <Link to="/settings/tools-catalog" style={{ color: 'var(--color-primary)' }}>Tool Catalog</Link> to activate integrations like Resend, Slack, or Stripe.</p>
              </div>
            )}

            {!toolsLoading && allTools.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {allTools.map(tool => {
                  const config = agentTools.find(at => at.toolId === tool.id);
                  const isAttached = !!config;
                  const isExpanded = expandedToolId === tool.id && isAttached;
                  const EXEC_COLOR: Record<string, string> = { HTTP: '#8b5cf6', CODE: '#f59e0b', DB_QUERY: '#06b6d4' };
                  const color = EXEC_COLOR[tool.executorType] ?? '#6366f1';
                  return (
                    <div key={tool.id} style={{
                      background: isAttached ? `${color}08` : 'var(--surface-2)',
                      border: `1px solid ${isAttached ? `${color}40` : 'var(--border)'}`,
                      borderRadius: 10,
                      overflow: 'hidden',
                      transition: 'all 0.2s',
                    }}>
                      {/* Header row */}
                      <div style={{
                        padding: '14px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                      }}>
                        {/* Icon */}
                        <div style={{
                          width: 36, height: 36, borderRadius: 8,
                          background: `${color}15`, border: `1px solid ${color}30`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color, flexShrink: 0,
                        }}>
                          {tool.executorType === 'HTTP' ? <Webhook size={16} /> : <Code2 size={16} />}
                        </div>

                        {/* Info — clickable to expand */}
                        <div
                          style={{ flex: 1, minWidth: 0, cursor: isAttached ? 'pointer' : 'default' }}
                          onClick={() => {
                            if (!isAttached) return;
                            if (isExpanded) {
                              setExpandedToolId(null);
                            } else {
                              setExpandedToolId(tool.id);
                              setEditToolInstructions(config?.instructions ?? '');
                              const dp = config?.defaultParams ?? {};
                              const mapped: Record<string,string> = {};
                              for (const [k,v] of Object.entries(dp)) mapped[k] = String(v ?? '');
                              setEditToolDefaultParams(mapped);
                            }
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 600, fontSize: 14 }}>{tool.displayName}</span>
                            <code style={{ fontSize: 10, color: 'var(--color-muted)', background: 'var(--surface-3)', padding: '1px 5px', borderRadius: 4 }}>{tool.toolName}</code>
                            {isAttached && config?.instructions && (
                              <span style={{ fontSize: 10, fontWeight: 600, background: '#10b98118', color: '#10b981', border: '1px solid #10b98133', borderRadius: 4, padding: '1px 6px' }}>HAS INSTRUCTIONS</span>
                            )}
                          </div>
                          {tool.description && (
                            <p style={{ fontSize: 12, color: 'var(--color-muted)', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tool.description}</p>
                          )}
                        </div>

                        {/* Expand chevron (only for attached) */}
                        {isAttached && (
                          <button
                            onClick={() => {
                              if (isExpanded) {
                                setExpandedToolId(null);
                              } else {
                                setExpandedToolId(tool.id);
                                setEditToolInstructions(config?.instructions ?? '');
                                const dp = config?.defaultParams ?? {};
                                const mapped: Record<string,string> = {};
                                for (const [k,v] of Object.entries(dp)) mapped[k] = String(v ?? '');
                                setEditToolDefaultParams(mapped);
                              }
                            }}
                            style={{
                              background: 'none', border: 'none', color: 'var(--color-muted)',
                              cursor: 'pointer', padding: 4, display: 'flex',
                              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)',
                              transition: 'transform 0.2s',
                            }}
                            title="Configure tool"
                          >
                            <ChevronDown size={16} />
                          </button>
                        )}

                        {/* Toggle */}
                        <button
                          id={`toggle-tool-${tool.id}`}
                          onClick={() => handleToggleTool(tool)}
                          style={{
                            background: isAttached ? `${color}18` : 'var(--surface-3)',
                            border: `1px solid ${isAttached ? `${color}40` : 'var(--border)'}`,
                            borderRadius: 6,
                            color: isAttached ? color : 'var(--color-muted)',
                            cursor: 'pointer',
                            padding: '5px 10px',
                            display: 'flex', alignItems: 'center', gap: 4,
                            fontSize: 11, fontWeight: 600,
                            transition: 'all 0.2s',
                          }}
                        >
                          {isAttached ? '● On' : '○ Off'}
                        </button>
                      </div>

                      {/* Expanded config panel */}
                      {isExpanded && (
                        <div style={{
                          padding: '0 16px 16px',
                          borderTop: '1px solid var(--border)',
                          marginTop: 0,
                          paddingTop: 16,
                        }}>
                          {/* Instructions */}
                          <div style={{ marginBottom: 16 }}>
                            <label style={{ fontSize: 12, fontWeight: 600, color: '#e2e8f0', display: 'block', marginBottom: 6 }}>
                              📝 Instructions — <span style={{ fontWeight: 400, color: 'var(--color-muted)' }}>when and how should the agent use this tool?</span>
                            </label>
                            <textarea
                              value={editToolInstructions}
                              onChange={e => setEditToolInstructions(e.target.value)}
                              placeholder={`Example: "Use this to send order confirmation emails after a refund is processed. Always CC ops@company.com."`}
                              rows={3}
                              style={{
                                width: '100%', boxSizing: 'border-box',
                                background: 'var(--surface-3)', border: '1px solid var(--border)',
                                borderRadius: 8, padding: '10px 12px',
                                color: 'var(--color-text)', fontSize: 13,
                                resize: 'vertical', fontFamily: 'inherit',
                              }}
                            />
                          </div>

                          {/* Default params — schema-driven fields */}
                          {(() => {
                            const schema = config?.tool?.inputSchema as { properties?: Record<string, { type?: string; description?: string; enum?: string[] }>; required?: string[] } | undefined;
                            const props = schema?.properties ?? {};
                            const required = new Set(schema?.required ?? []);
                            const paramKeys = Object.keys(props);
                            if (paramKeys.length === 0) return null;
                            return (
                              <div style={{ marginBottom: 16 }}>
                                <label style={{ fontSize: 12, fontWeight: 600, color: '#e2e8f0', display: 'block', marginBottom: 8 }}>
                                  📌 Default Parameters — <span style={{ fontWeight: 400, color: 'var(--color-muted)' }}>pre-fill values the agent will use when calling this tool</span>
                                </label>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                  {paramKeys.map(key => {
                                    const prop = props[key];
                                    const isRequired = required.has(key);
                                    return (
                                      <div key={key}>
                                        <label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                          <code style={{ fontSize: 11, background: 'var(--surface-3)', padding: '1px 5px', borderRadius: 4, color: '#e2e8f0' }}>{key}</code>
                                          {isRequired && <span style={{ fontSize: 9, fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase' }}>required</span>}
                                          <span style={{ fontSize: 10, color: '#64748b', fontWeight: 400 }}>{prop.type ?? ''}</span>
                                        </label>
                                        {prop.description && (
                                          <p style={{ fontSize: 11, color: '#64748b', margin: '0 0 4px', lineHeight: 1.4 }}>{prop.description}</p>
                                        )}
                                        {prop.enum ? (
                                          <select
                                            value={editToolDefaultParams[key] ?? ''}
                                            onChange={e => setEditToolDefaultParams(prev => ({ ...prev, [key]: e.target.value }))}
                                            style={{
                                              width: '100%', boxSizing: 'border-box',
                                              background: 'var(--surface-3)', border: '1px solid var(--border)',
                                              borderRadius: 6, padding: '7px 10px',
                                              color: 'var(--color-text)', fontSize: 12,
                                            }}
                                          >
                                            <option value="">— leave for agent to decide —</option>
                                            {prop.enum.map(v => <option key={v} value={v}>{v}</option>)}
                                          </select>
                                        ) : (
                                          <input
                                            type="text"
                                            value={editToolDefaultParams[key] ?? ''}
                                            onChange={e => setEditToolDefaultParams(prev => ({ ...prev, [key]: e.target.value }))}
                                            placeholder={`Leave blank to let the agent decide`}
                                            style={{
                                              width: '100%', boxSizing: 'border-box',
                                              background: 'var(--surface-3)', border: '1px solid var(--border)',
                                              borderRadius: 6, padding: '7px 10px',
                                              color: 'var(--color-text)', fontSize: 12,
                                            }}
                                          />
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })()}

                          {/* Save button */}
                          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                              id={`save-tool-config-${tool.id}`}
                              disabled={savingToolConfig}
                              onClick={async () => {
                                setSavingToolConfig(true);
                                try {
                                  // Build params — only include non-empty values
                                  const cleaned: Record<string, string> = {};
                                  for (const [k, v] of Object.entries(editToolDefaultParams)) {
                                    if (v.trim()) cleaned[k] = v.trim();
                                  }
                                  const defaultParams = Object.keys(cleaned).length > 0 ? cleaned : null;
                                  await agentToolApi.update(agentId, tool.id, {
                                    instructions: editToolInstructions.trim() || null,
                                    defaultParams,
                                  });
                                  notifySuccess('Configuration saved', `"${tool.displayName}" configuration updated.`);
                                  void loadTools();
                                } catch (e) {
                                  notifyError('Failed to save', (e as Error).message);
                                } finally {
                                  setSavingToolConfig(false);
                                }
                              }}
                              style={{
                                background: 'var(--color-primary)', color: '#fff',
                                border: 'none', borderRadius: 6,
                                padding: '7px 16px', fontSize: 12, fontWeight: 600,
                                cursor: savingToolConfig ? 'not-allowed' : 'pointer',
                                display: 'flex', alignItems: 'center', gap: 6,
                                opacity: savingToolConfig ? 0.6 : 1,
                              }}
                            >
                              <Save size={13} />
                              {savingToolConfig ? 'Saving…' : 'Save Configuration'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Skills tab */}
        {tab === 'skills' && (
          <div className={styles.panel} style={{ maxWidth: 800 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
              <div>
                <h2 className={styles.panelTitle} style={{ marginBottom: 4 }}>Agent Skills</h2>
                <p className={styles.hint}>Skills are actions this agent can perform during conversations. Attach skills from your library to enable them.</p>
              </div>
              <button
                id="add-skill-btn"
                className={styles.saveBtn}
                style={{ marginTop: 0, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}
                onClick={() => setShowSkillPicker(true)}
                disabled={skillsLoading}
              >
                <Plus size={14} /> Add Skill
              </button>
            </div>

            {/* Attached skills list */}
            {skillsLoading && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[1,2,3].map(i => <div key={i} className={`${styles.panel}`} style={{ height: 72, borderRadius: 10, background: 'var(--surface-2)', opacity: 0.5 }} />)}
              </div>
            )}

            {!skillsLoading && agentSkills.length === 0 && !showSkillPicker && (
              <div style={{
                border: '2px dashed var(--border)',
                borderRadius: 12,
                padding: '2.5rem',
                textAlign: 'center',
                color: 'var(--color-muted)',
              }}>
                <Wrench size={32} style={{ margin: '0 auto 12px', opacity: 0.4, display: 'block' }} />
                <p style={{ fontWeight: 600, marginBottom: 6 }}>No skills attached</p>
                <p style={{ fontSize: 13 }}>Add skills to let this agent search knowledge, call APIs, or execute custom logic.</p>
                <button
                  className={styles.saveBtn}
                  style={{ margin: '16px auto 0', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  onClick={() => setShowSkillPicker(true)}
                >
                  <Plus size={14} /> Attach your first skill
                </button>
              </div>
            )}

            {!skillsLoading && agentSkills.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {agentSkills.map(cfg => {
                  const IMPL_ICON: Record<string, React.ReactNode> = {
                    KNOWLEDGE_RETRIEVAL: <BookOpen size={14} />,
                    EXTERNAL_API:        <Webhook size={14} />,
                    CODE:                <Code2 size={14} />,
                    PROMPT_ONLY:         <Zap size={14} />,
                  };
                  const RISK_COLOR: Record<string, string> = {
                    LOW:      '#10b981',
                    MEDIUM:   '#f59e0b',
                    HIGH:     '#ef4444',
                    CRITICAL: '#dc2626',
                  };
                  const risk = cfg.skill.riskLevel;
                  return (
                    <div key={cfg.id} style={{
                      background: 'var(--surface-2)',
                      border: '1px solid var(--border)',
                      borderRadius: 10,
                      padding: '14px 16px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 12,
                    }}>
                      <div style={{
                        width: 36, height: 36,
                        borderRadius: 8,
                        background: 'var(--surface-3)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'var(--color-primary)',
                        flexShrink: 0,
                      }}>
                        {IMPL_ICON[cfg.skill.implementationType] ?? <Wrench size={14} />}
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 600, fontSize: 14 }}>{cfg.skill.name}</span>
                          <span style={{
                            fontSize: 10, fontWeight: 700,
                            background: `${RISK_COLOR[risk]}22`,
                            color: RISK_COLOR[risk],
                            border: `1px solid ${RISK_COLOR[risk]}44`,
                            borderRadius: 4, padding: '1px 6px',
                          }}>{risk} RISK</span>
                          {cfg.skill.approvalRequired && (
                            <span style={{
                              fontSize: 10, fontWeight: 700,
                              background: '#6366f122',
                              color: '#818cf8',
                              border: '1px solid #6366f144',
                              borderRadius: 4, padding: '1px 6px',
                              display: 'flex', alignItems: 'center', gap: 3,
                            }}>
                              <ShieldCheck size={10} /> APPROVAL REQUIRED
                            </span>
                          )}
                          {!cfg.enabled && (
                            <span style={{
                              fontSize: 10, fontWeight: 700,
                              background: '#94a3b822',
                              color: '#94a3b8',
                              border: '1px solid #94a3b844',
                              borderRadius: 4, padding: '1px 6px',
                            }}>DISABLED</span>
                          )}
                        </div>
                        {cfg.skill.description && (
                          <p style={{ fontSize: 12, color: 'var(--color-muted)', marginTop: 4 }}>{cfg.skill.description}</p>
                        )}
                        {cfg.skill.systemPromptInstructions && (
                          <p style={{ fontSize: 11, color: '#6366f1', marginTop: 6, fontStyle: 'italic' }}>
                            "{cfg.skill.systemPromptInstructions.slice(0, 100)}{cfg.skill.systemPromptInstructions.length > 100 ? '…' : ''}"
                          </p>
                        )}
                      </div>

                      <div style={{ display: 'flex', gap: 7, flexShrink: 0, alignItems: 'center' }}>  
                        {/* Enable/Disable toggle */}
                        <button
                          id={`toggle-skill-${cfg.skillId}`}
                          title={cfg.enabled ? 'Disable skill for this agent' : 'Enable skill for this agent'}
                          onClick={() => handleToggleSkill(cfg.skillId, cfg.enabled)}
                          style={{
                            background: cfg.enabled ? '#10b98118' : 'var(--surface-3)',
                            border: `1px solid ${cfg.enabled ? '#10b98133' : 'var(--border)'}`,
                            borderRadius: 6,
                            color: cfg.enabled ? '#10b981' : 'var(--color-muted)',
                            cursor: 'pointer',
                            padding: '5px 8px',
                            display: 'flex', alignItems: 'center', gap: 4,
                            fontSize: 11, fontWeight: 600,
                          }}
                        >
                          {cfg.enabled ? '● On' : '○ Off'}
                        </button>
                        {/* Skill detail link */}
                        <Link
                          to="/skills/$skillId"
                          params={{ skillId: cfg.skillId }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 4,
                            padding: '5px 9px', borderRadius: 6,
                            background: 'var(--surface-3)', border: '1px solid var(--border)',
                            color: 'var(--color-muted)', fontSize: 11,
                            textDecoration: 'none',
                          }}
                          title="Open skill detail"
                        >
                          <ArrowRight size={11} />
                        </Link>
                        {/* Remove */}
                        <button
                          id={`remove-skill-${cfg.skillId}`}
                          title="Remove skill from this agent"
                          onClick={() => handleDetachSkill(cfg.skillId)}
                          style={{
                            background: 'none',
                            border: '1px solid var(--border)',
                            borderRadius: 6,
                            color: '#ef4444',
                            cursor: 'pointer',
                            padding: '5px 8px',
                            display: 'flex', alignItems: 'center', gap: 4,
                            fontSize: 11,
                          }}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Skill picker modal */}
            {showSkillPicker && (
              <div style={{
                position: 'fixed', inset: 0,
                background: 'rgba(0,0,0,0.6)',
                zIndex: 1000,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 24,
              }} onClick={() => setShowSkillPicker(false)}>
                <div style={{
                  background: 'var(--surface-1)',
                  border: '1px solid var(--border)',
                  borderRadius: 16,
                  width: '100%',
                  maxWidth: 580,
                  maxHeight: '80vh',
                  overflow: 'hidden',
                  display: 'flex', flexDirection: 'column',
                }} onClick={e => e.stopPropagation()}>
                  <div style={{ padding: '20px 20px 12px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h3 style={{ fontWeight: 700, fontSize: 16, margin: 0 }}>Attach a skill</h3>
                      <p style={{ fontSize: 12, color: 'var(--color-muted)', margin: '4px 0 0' }}>Choose a skill from your library to attach to this agent</p>
                    </div>
                    <button onClick={() => setShowSkillPicker(false)} style={{ background: 'none', border: 'none', color: 'var(--color-muted)', cursor: 'pointer', fontSize: 20 }}>×</button>
                  </div>
                  <div style={{ overflow: 'auto', padding: 16 }}>
                    {allSkills.filter(s => !agentSkills.some(a => a.skillId === s.id)).length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-muted)' }}>
                        {allSkills.length === 0 ? (
                          <>
                            <Wrench size={28} style={{ margin: '0 auto 10px', display: 'block', opacity: 0.4 }} />
                            <p style={{ fontWeight: 600 }}>No skills in your library</p>
                            <p style={{ fontSize: 12 }}>Go to <Link to="/skills" style={{ color: 'var(--color-primary)' }}>Skills</Link> to create your first skill.</p>
                          </>
                        ) : (
                          <>
                            <CheckCircle size={28} style={{ margin: '0 auto 10px', display: 'block', color: '#10b981' }} />
                            <p style={{ fontWeight: 600 }}>All skills are already attached!</p>
                          </>
                        )}
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {allSkills
                          .filter(s => !agentSkills.some(a => a.skillId === s.id))
                          .map(skill => {
                            const RISK_COLOR: Record<string, string> = { LOW: '#10b981', MEDIUM: '#f59e0b', HIGH: '#ef4444', CRITICAL: '#dc2626' };
                            const IMPL_COLOR: Record<string, string> = { KNOWLEDGE_RETRIEVAL: '#06b6d4', EXTERNAL_API: '#8b5cf6', CODE: '#f59e0b', PROMPT_ONLY: '#10b981' };
                            const IMPL_LABEL: Record<string, string> = { KNOWLEDGE_RETRIEVAL: 'Knowledge', EXTERNAL_API: 'API', CODE: 'Code', PROMPT_ONLY: 'Prompt' };
                            const implColor = IMPL_COLOR[skill.implementationType] ?? '#6366f1';
                            return (
                              <button
                                key={skill.id}
                                id={`pick-skill-${skill.id}`}
                                onClick={() => handleAttachSkill(skill.id)}
                                disabled={!!attachingSkillId}
                                style={{
                                  background: attachingSkillId === skill.id ? `${implColor}18` : 'var(--surface-2)',
                                  border: `1px solid ${attachingSkillId === skill.id ? implColor : 'var(--border)'}`,
                                  borderRadius: 10,
                                  padding: '12px 14px',
                                  textAlign: 'left',
                                  cursor: attachingSkillId ? 'not-allowed' : 'pointer',
                                  display: 'flex', alignItems: 'center', gap: 12,
                                  transition: 'border-color 0.2s, background 0.2s',
                                  width: '100%',
                                  color: 'var(--color-text)',
                                  opacity: attachingSkillId && attachingSkillId !== skill.id ? 0.4 : 1,
                                }}
                                onMouseEnter={e => { if (!attachingSkillId) { e.currentTarget.style.borderColor = implColor; e.currentTarget.style.background = `${implColor}0a`; } }}
                                onMouseLeave={e => { if (!attachingSkillId) { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--surface-2)'; } }}
                              >
                                {/* Type icon */}
                                <div style={{ width: 34, height: 34, borderRadius: 8, background: `${implColor}18`, border: `1px solid ${implColor}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: implColor, flexShrink: 0, fontSize: 11, fontWeight: 700 }}>
                                  {IMPL_LABEL[skill.implementationType]?.slice(0, 2) ?? '?'}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  {/* Name — explicitly white so it's visible on dark background */}
                                  <div style={{ fontWeight: 700, fontSize: 14, color: '#f1f5f9', marginBottom: 4 }}>{skill.name}</div>
                                  <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: 10, color: implColor, fontWeight: 600 }}>{IMPL_LABEL[skill.implementationType]}</span>
                                    <span style={{ fontSize: 10, fontWeight: 700, background: `${RISK_COLOR[skill.riskLevel]}22`, color: RISK_COLOR[skill.riskLevel], border: `1px solid ${RISK_COLOR[skill.riskLevel]}44`, borderRadius: 4, padding: '1px 6px' }}>{skill.riskLevel}</span>
                                    {skill.approvalRequired && <span style={{ fontSize: 10, background: '#6366f122', color: '#818cf8', border: '1px solid #6366f144', borderRadius: 4, padding: '1px 6px', fontWeight: 700 }}>APPROVAL</span>}
                                  </div>
                                  {skill.description && <p style={{ fontSize: 12, color: '#94a3b8', margin: '4px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{skill.description}</p>}
                                </div>
                                <Shield size={14} style={{ color: RISK_COLOR[skill.riskLevel], flexShrink: 0 }} />
                              </button>
                            );
                          })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {/* Flow execution map tab */}
        {tab === 'flow' && (
          <div className={styles.panel} style={{ maxWidth: 800 }}>
            <h2 className={styles.panelTitle}>Agent Execution Flow Map</h2>
            <p className={styles.hint} style={{ marginBottom: 24 }}>
              Visual diagram of the execution flow from the incoming user message to the final agent response, highlighting active skills, tools, and guardrails.
            </p>

            {flowLoading ? (
              <div className={styles.loading}>
                <RefreshCw size={24} className="animate-spin" />
                <span>Loading execution flow...</span>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                {/* STEP 1: INPUT */}
                <div style={{
                  width: '100%',
                  maxWidth: 550,
                  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(129, 140, 248, 0.03) 100%)',
                  border: '1px dashed #6366f160',
                  borderRadius: 12,
                  padding: '16px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%',
                    background: 'rgba(99,102,241,0.15)', border: '1px solid #6366f130',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#818cf8', flexShrink: 0
                  }}>
                    <MessageSquare size={18} />
                  </div>
                  <div>
                    <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#a5b4fc', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Step 1: Incoming User Message</h4>
                    <p style={{ margin: '4px 0 0', fontSize: 13, color: '#94a3b8' }}>
                      Triggers via active channels: <strong style={{ color: '#cbd5e1' }}>{agent.routingChannels.length > 0 ? agent.routingChannels.join(', ') : 'All channels'}</strong>
                    </p>
                  </div>
                </div>

                {/* Arrow 1 */}
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6366f140" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '4px auto' }}>
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <polyline points="19 12 12 19 5 12" />
                </svg>

                {/* STEP 2: ORCHESTRATOR */}
                <div style={{
                  width: '100%',
                  maxWidth: 550,
                  background: 'var(--surface-2)',
                  border: '1px solid var(--border)',
                  borderRadius: 12,
                  padding: '18px 20px',
                  display: 'flex',
                  gap: 16,
                }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%',
                    background: 'var(--color-primary-dim)', border: '1px solid var(--color-primary)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'var(--color-primary)', flexShrink: 0
                  }}>
                    <Bot size={18} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#e2e8f0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Step 2: AI Core Orchestration</h4>
                    <div style={{ fontSize: 13, color: '#cbd5e1', marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div>• <strong>Agent Identity</strong>: {agent.name}</div>
                      {agent.orchestrationConfig?.systemPrompt && (
                        <div>• <strong>System Instructions</strong>: <span style={{ fontStyle: 'italic', fontSize: 12, color: 'var(--color-muted)' }}>"{agent.orchestrationConfig.systemPrompt.slice(0, 80)}..."</span></div>
                      )}
                      <div>• <strong>Auto-Escalation Threshold</strong>: {agent.orchestrationConfig?.maxTurnsBeforeEscalation ?? 10} turn limit</div>
                    </div>
                  </div>
                </div>

                {/* Arrow 2 */}
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6366f140" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '4px auto' }}>
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <polyline points="19 12 12 19 5 12" />
                </svg>

                {/* STEP 3: CAPABILITIES (SKILLS) */}
                <div style={{
                  width: '100%',
                  maxWidth: 550,
                  background: 'var(--surface-2)',
                  border: '1px solid var(--border)',
                  borderRadius: 12,
                  padding: '18px 20px',
                  display: 'flex',
                  gap: 16,
                }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%',
                    background: 'rgba(245, 158, 11, 0.15)', border: '1px solid rgba(245, 158, 11, 0.3)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#f59e0b', flexShrink: 0
                  }}>
                    <Wrench size={18} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#e2e8f0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Step 3: Attached Skills & Tools</h4>
                    {agentSkills.length === 0 ? (
                      <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--color-muted)', fontStyle: 'italic' }}>
                        No active skills configured. The agent will respond purely using its system prompt guidelines.
                      </p>
                    ) : (
                      <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {agentSkills.map(cfg => {
                          const riskColors = { LOW: '#10b981', MEDIUM: '#f59e0b', HIGH: '#ef4444', CRITICAL: '#dc2626' };
                          return (
                            <div key={cfg.id} style={{
                              background: 'var(--surface-3)',
                              border: '1px solid var(--border)',
                              borderRadius: 8,
                              padding: '10px 12px',
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
                                <span style={{ fontSize: 13, fontWeight: 600 }}>{cfg.skill.name}</span>
                                <span style={{ fontSize: 9, padding: '1px 5px', border: `1px solid ${riskColors[cfg.skill.riskLevel]}40`, background: `${riskColors[cfg.skill.riskLevel]}15`, color: riskColors[cfg.skill.riskLevel], borderRadius: 4, fontWeight: 700 }}>
                                  {cfg.skill.riskLevel}
                                </span>
                              </div>
                              <div style={{ display: 'flex', gap: 12, marginTop: 4, fontSize: 11, color: 'var(--color-muted)' }}>
                                <span>Type: {cfg.skill.implementationType}</span>
                                {cfg.skill.toolName && <span>Tool: <code>{cfg.skill.toolName}</code></span>}
                                {cfg.skill.approvalRequired && <span style={{ color: '#f59e0b' }}>⚠️ Approval required</span>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Arrow 3 */}
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6366f140" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '4px auto' }}>
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <polyline points="19 12 12 19 5 12" />
                </svg>

                {/* STEP 4: GUARDRAILS */}
                <div style={{
                  width: '100%',
                  maxWidth: 550,
                  background: 'var(--surface-2)',
                  border: '1px solid var(--border)',
                  borderRadius: 12,
                  padding: '18px 20px',
                  display: 'flex',
                  gap: 16,
                }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%',
                    background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.3)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#ef4444', flexShrink: 0
                  }}>
                    <ShieldCheck size={18} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#e2e8f0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Step 4: Safety & Policy Guardrails</h4>
                    {allGuardrails.filter(g => g.active && (g.agentIds.length === 0 || g.agentIds.includes(agent.id))).length === 0 ? (
                      <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--color-muted)', fontStyle: 'italic' }}>
                        No active guardrails applied to this agent.
                      </p>
                    ) : (
                      <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {allGuardrails
                          .filter(g => g.active && (g.agentIds.length === 0 || g.agentIds.includes(agent.id)))
                          .map(g => (
                            <div key={g.id} style={{
                              background: 'var(--surface-3)',
                              border: '1px solid var(--border)',
                              borderRadius: 8,
                              padding: '10px 12px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                            }}>
                              <div>
                                <div style={{ fontSize: 13, fontWeight: 600 }}>{g.name}</div>
                                <div style={{ fontSize: 11, color: 'var(--color-muted)', marginTop: 2 }}>
                                  Type: {g.ruleType} · Severity: <span style={{ color: g.severity === 'CRITICAL' ? '#ef4444' : '#f59e0b', fontWeight: 600 }}>{g.severity}</span>
                                </div>
                              </div>
                              <span style={{ fontSize: 10, background: g.agentIds.length === 0 ? '#10b98120' : '#6366f120', color: g.agentIds.length === 0 ? '#10b981' : '#818cf8', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>
                                {g.agentIds.length === 0 ? 'GLOBAL' : 'AGENT-SPECIFIC'}
                              </span>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Arrow 4 */}
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6366f140" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '4px auto' }}>
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <polyline points="19 12 12 19 5 12" />
                </svg>

                {/* STEP 5: OUTPUT */}
                <div style={{
                  width: '100%',
                  maxWidth: 550,
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(16, 185, 129, 0.02) 100%)',
                  border: '1px dashed #10b98160',
                  borderRadius: 12,
                  padding: '16px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%',
                    background: 'rgba(16,185,129,0.15)', border: '1px solid #10b98130',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#10b981', flexShrink: 0
                  }}>
                    <Send size={18} />
                  </div>
                  <div>
                    <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#a7f3d0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Step 5: Final Response Delivery</h4>
                    <p style={{ margin: '4px 0 0', fontSize: 13, color: '#94a3b8' }}>
                      Delivers safe, contextual responses or triggers operator handoff escalations.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Test console tab */}
        {tab === 'test' && (
          <div className={styles.testLayout}>
            {/* Chat panel */}
            <div className={styles.chatPanel}>
              <div className={styles.chatHeader}>
                <MessageSquare size={16} />
                <span>Test Console</span>
                {testConv && <span className={styles.convId}>#{testConv.id.slice(0, 8)}</span>}
                <div style={{ flex: 1 }} />
                <button
                  id="start-test"
                  className={styles.startBtn}
                  onClick={handleStartTest}
                  disabled={testLoading}
                >
                  {testConv ? '↺ Reset' : '▶ Start conversation'}
                </button>
              </div>

              {!testConv && (
                <div className={styles.chatEmpty}>
                  <Play size={36} className={styles.emptyIcon} />
                  <p>Click "Start conversation" to begin testing this agent</p>
                </div>
              )}

              {testConv && (
                <>
                  <div className={styles.messages}>
                    {testConv.messages
                      .filter(m => m.content?.trim())
                      .map(m => {
                        const isAgent = m.senderType.toLowerCase() === 'agent';
                        return (
                          <div key={m.id} className={`${styles.message} ${styles[`msg_${m.senderType.toLowerCase()}`]}`}>
                            <div className={styles.msgBubble}>
                              {isAgent ? (
                                <div
                                  className={styles.msgMarkdown}
                                  dangerouslySetInnerHTML={{ __html: renderMarkdown(m.content ?? '') }}
                                />
                              ) : (
                                <p className={styles.msgContent}>{m.content}</p>
                              )}
                              <span className={styles.msgTime}>{new Date(m.createdAt).toLocaleTimeString()}</span>
                            </div>
                          </div>
                        );
                      })}

                    {testLoading && (
                      <div className={`${styles.message} ${styles.msg_agent}`}>
                        <div className={styles.msgBubble}>
                          <div className={styles.typing}><span /><span /><span /></div>
                        </div>
                      </div>
                    )}
                    {testError && (
                      <div style={{
                        margin: '8px 12px',
                        padding: '10px 12px',
                        borderRadius: 8,
                        background: '#ef444411',
                        border: '1px solid #ef444433',
                        borderLeft: '3px solid #ef4444',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#ef4444', fontWeight: 600, fontSize: 12 }}>
                          <AlertTriangle size={12} /> Agent error
                        </div>
                        <p style={{ color: '#fca5a5', fontSize: 12, margin: '4px 0 0', lineHeight: 1.4 }}>{testError.message}</p>
                        {testError.resolutionHint && (
                          <p style={{ color: '#f97316', fontSize: 11, margin: '6px 0 0', lineHeight: 1.4 }}>
                            💡 {testError.resolutionHint}
                          </p>
                        )}
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  <div className={styles.inputRow}>
                    <input
                      id="test-message-input"
                      className={styles.chatInput}
                      placeholder="Type a message…"
                      value={testInput}
                      onChange={e => setTestInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void handleSendTest(); } }}
                      disabled={testLoading}
                    />
                    <button
                      id="send-test-message"
                      className={styles.sendBtn}
                      onClick={handleSendTest}
                      disabled={testLoading || !testInput.trim()}
                    >
                      <Send size={16} />
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Event log panel */}
            <div className={styles.eventPanel}>
              <div className={styles.eventHeader}>
                <Hash size={14} />
                <span>Event Log</span>
                <span className={styles.eventCount}>{events.length}</span>
              </div>
              <div className={styles.eventList}>
                {events.length === 0 && (
                  <p className={styles.eventEmpty}>Events will appear here as the conversation progresses.</p>
                )}
                {events.map(ev => (
                  <div key={ev.id} className={styles.eventItem}>
                    <div className={styles.eventDot} style={{ '--c': EVENT_COLORS[ev.eventType] ?? 'var(--color-muted)' } as React.CSSProperties}>
                      {EVENT_ICONS[ev.eventType]}
                    </div>
                    <div className={styles.eventInfo}>
                      <span className={styles.eventType}>{ev.eventType.replace(/_/g, ' ')}</span>
                      <div className={styles.eventMeta}>
                        {ev.durationMs != null && (
                          <span><Clock size={10} /> {ev.durationMs}ms</span>
                        )}
                        {ev.costUnits != null && ev.costUnits > 0 && (
                          <span><Zap size={10} /> {ev.costUnits} tokens</span>
                        )}
                      </div>
                    </div>
                    <span className={styles.eventSeq}>#{ev.sequenceNumber}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
